// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  ClipboardList,
  FileBadge,
  ListChecks,
  Mail,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TicketCheck,
  UserRound,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Card, PageHeader, formatRelative, useToast } from "@chotca/ui";
import { type Org, type PendingApplicant } from "@chotca/config";
import { DashboardCards } from "@/components/employer/dashboard-cards";
import { PlanStatusCard } from "@/components/employer/plan-status-card";
import { useMe } from "@chotca/api-client";
import { useMyPostings } from "@chotca/api-client";
import { usePendingApplicants } from "@chotca/api-client";
import { useOrg } from "@chotca/api-client";
import { usePublicPricing } from "@chotca/api-client";
import { getSdk } from "@chotca/api-client";
import {
  getCompletionSummary,
  type CompletionTask,
} from "@/lib/employer/profile-completion";

const TODO_LIMIT = 10;
const PTR_THRESHOLD = 72;
const PTR_MAX = 110;

// Custom pull-to-refresh: web has no native equivalent on most browsers, but
// our employer audience is mostly on phones. Trigger only when the document
// is already scrolled to the top and the gesture is a clear vertical drag —
// horizontal swipes pass through untouched so they don't fight any nested
// carousels.
function usePullToRefresh(onRefresh: () => Promise<void>) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const startY = React.useRef<number | null>(null);
  const [pull, setPull] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshing) return;
      if (window.scrollY > 0) return;
      const t0 = e.touches[0];
      if (!t0) return;
      startY.current = t0.clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (refreshing || startY.current == null) return;
      const t0 = e.touches[0];
      if (!t0) return;
      const dy = t0.clientY - startY.current;
      if (dy <= 0) {
        setPull(0);
        return;
      }
      // Rubber-band damping past the threshold.
      const eased =
        dy < PTR_THRESHOLD ? dy : PTR_THRESHOLD + (dy - PTR_THRESHOLD) * 0.4;
      setPull(Math.min(eased, PTR_MAX));
    };
    const onTouchEnd = async () => {
      const dy = pull;
      startY.current = null;
      if (dy >= PTR_THRESHOLD) {
        setRefreshing(true);
        setPull(PTR_THRESHOLD);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pull, refreshing, onRefresh]);

  return { containerRef, pull, refreshing, threshold: PTR_THRESHOLD };
}

function PullIndicator({
  pull,
  threshold,
  active,
}: {
  pull: number;
  threshold: number;
  active: boolean;
}) {
  if (pull <= 0 && !active) return null;
  const progress = Math.min(pull / threshold, 1);
  const ready = pull >= threshold || active;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-0 right-0 -top-12 flex justify-center md:hidden"
      style={{ opacity: progress }}
    >
      <span
        className={
          "grid h-9 w-9 place-items-center rounded-full bg-paper shadow-sm ring-1 ring-smoke " +
          (active ? "animate-spin" : "")
        }
        style={
          !active ? { transform: `rotate(${progress * 360}deg)` } : undefined
        }
      >
        <ArrowRight
          size={16}
          className={
            "text-ink transition-transform " +
            (ready ? "rotate-90" : "rotate-90")
          }
        />
      </span>
    </div>
  );
}

export function DashboardClient() {
  const t = useTranslations("dashboard");
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId;
  const org = useOrg(orgId);
  const postings = useMyPostings();
  const pending = usePendingApplicants(TODO_LIMIT);
  const pricing = usePublicPricing();

  const refresh = React.useCallback(async () => {
    await Promise.all([
      me.refetch(),
      org.refetch(),
      postings.refetch(),
      pending.refetch(),
    ]);
  }, [me, org, postings, pending]);
  const ptr = usePullToRefresh(refresh);

  const hasAnyPosting =
    !postings.isLoading && (postings.data?.items.length ?? 0) > 0;
  const applicantsTotal = pending.data?.totalCount ?? 0;
  const completion = getCompletionSummary(org.data);

  const emailUnverified = !!me.data?.email && !me.data.emailVerifiedAt;
  // Profile is "done" when there are no outstanding completion tasks and the
  // email is verified — that flips the dashboard cards into their expanded
  // (5-latest-per-card) layout and drops the completion banner.
  const profileComplete =
    !emailUnverified && completion.done >= completion.total;
  const { success: toastSuccess, error: toastError } = useToast();
  const tAuth = useTranslations("auth");
  const resendEmail = useMutation({
    mutationFn: () => getSdk().auth.requestEmailVerify(),
    onSuccess: () => toastSuccess(tAuth("verifyEmailResendSent")),
    onError: () => toastError(tAuth("verifyEmailFailed")),
  });

  return (
    <>
      <div
        ref={ptr.containerRef}
        style={{ transform: `translateY(${ptr.pull}px)` }}
        className="relative flex flex-col gap-6 pb-24 transition-transform md:pb-0"
      >
        <PullIndicator
          pull={ptr.pull}
          threshold={ptr.threshold}
          active={ptr.refreshing}
        />
        <PageHeader
          className="hidden md:flex"
          title={t("title")}
          actions={
            <Link
              to="/postings/new"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-ink/90"
            >
              <Plus size={16} />
              {t("ctaCreatePosting")}
            </Link>
          }
        />

        {/* Profile completion stays pinned to the top until it is done. */}
        <div className="order-1">
          <CompletionCard
            loading={org.isLoading}
            summary={completion}
            kycRejectedHandledByBanner
            emailUnverified={emailUnverified}
            email={me.data?.email ?? null}
            onResendEmail={() => resendEmail.mutate()}
            resendingEmail={resendEmail.isPending}
            verifyBonus={pricing.data?.verifyBonus ?? 0}
          />
        </div>

        <KycRejectedBanner org={org.data} />

        <VerifyIdentityBanner identityLevel={me.data?.identityLevel} />

        <WardUpdateBanner org={org.data} />

        {/* Current plan tier + quota + upgrade CTA. */}
        {orgId ? (
          <section className="order-1">
            <PlanStatusCard orgId={orgId} />
          </section>
        ) : null}

        {!hasAnyPosting && !postings.isLoading ? (
          <section className="order-2">
            <Card
              tone="paper"
              padding="none"
              bordered
              className="overflow-hidden shadow-none"
            >
              <OnboardingPanel />
            </Card>
          </section>
        ) : (
          <>
            <section className="order-2">
              <DashboardCards
                applicantsTotal={applicantsTotal}
                loading={{
                  postings: postings.isLoading,
                  applicants: pending.isLoading,
                }}
              />
            </section>

            {/* Mobile-only "Cần xử lý" feed. Desktop already surfaces pending
                applicants inside the expanded cards, so it stays hidden there.
                Shown once the profile is complete — until then the completion
                card owns the top of the screen. */}
            {profileComplete ? (
              <section className="order-3 md:hidden">
                <NeedsActionFeed
                  items={pending.data?.items ?? []}
                  totalCount={pending.data?.totalCount ?? 0}
                  loading={pending.isLoading}
                />
              </section>
            ) : null}
          </>
        )}
      </div>

      {hasAnyPosting ? (
        <Link
          to="/postings/new"
          aria-label={t("ctaCreatePosting")}
          className="fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] right-4 z-40 inline-flex h-14 items-center gap-2 rounded-full bg-ink px-5 text-sm font-semibold text-paper shadow-[0_8px_24px_rgba(15,15,15,0.25)] transition active:scale-95 md:hidden"
        >
          <Plus size={18} strokeWidth={2.25} />
          {t("ctaCreatePosting")}
        </Link>
      ) : null}
    </>
  );
}

// — Banners ——————————————————————————————————————————————————————————————

function KycRejectedBanner({ org }: { org: Org | undefined }) {
  const t = useTranslations("dashboard");
  if (!org || org.kycStatus !== "REJECTED") return null;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-start gap-3 rounded-2xl border-[1.5px] border-danger/30 bg-danger/5 px-4 py-3 sm:flex-nowrap"
    >
      <AlertTriangle size={20} className="mt-0.5 shrink-0 text-danger" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">
          {t("kycRejectedTitle")}
        </p>
        {org.kycRejectReason ? (
          <p className="mt-0.5 text-xs text-ash">{org.kycRejectReason}</p>
        ) : null}
      </div>
      <Link
        to="/org/kyc"
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-paper transition hover:bg-ink/90"
      >
        {t("kycRejectedCta")}
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// Nudge employers whose branches predate the 2025 ward migration (wardCode
// null) to re-save their address with the new phường/xã. API sets
// `addressNeedsWardUpdate`; clears once every active branch has a ward.
function WardUpdateBanner({ org }: { org: Org | undefined }) {
  if (!org?.addressNeedsWardUpdate) return null;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-start gap-3 rounded-2xl border-[1.5px] border-sun/40 bg-sun/10 px-4 py-3 sm:flex-nowrap"
    >
      <AlertTriangle size={20} className="mt-0.5 shrink-0 text-ink/70" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">
          Cập nhật địa chỉ theo phường / xã mới
        </p>
        <p className="mt-0.5 text-xs text-ash">
          Địa chỉ của bạn đang theo địa giới cũ (quận / huyện). Từ 01/07/2025
          Việt Nam chuyển sang phường / xã — hãy mở địa chỉ và chọn lại để cập
          nhật.
        </p>
      </div>
      <Link
        to="/org"
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-paper transition hover:bg-ink/90"
      >
        Cập nhật ngay
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// Nudge unverified employers to reach U1 (SCRUM-74). Shown while the account is
// U0_NEW — verifying unlocks viewing applicant contact + posting. Non-blocking;
// the hard gate lives on the applicants screen + posting create.
function VerifyIdentityBanner({
  identityLevel,
}: {
  identityLevel?: "U0_NEW" | "U1_CONTACT" | "U2_IDENTITY";
}) {
  if (!identityLevel || identityLevel !== "U0_NEW") return null;
  return (
    <div
      role="alert"
      className="flex flex-wrap items-start gap-3 rounded-2xl border-[1.5px] border-sun/40 bg-sun/10 px-4 py-3 sm:flex-nowrap"
    >
      <ShieldAlert size={20} className="mt-0.5 shrink-0 text-ink/70" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">
          Xác minh tài khoản để đăng tin và xem liên hệ ứng viên
        </p>
        <p className="mt-0.5 text-xs text-ash">
          Chỉ mất một phút: xác minh email và số điện thoại của bạn.
        </p>
      </div>
      <Link
        to="/profile"
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-paper transition hover:bg-ink/90"
      >
        Xác minh ngay
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// — Profile completion ——————————————————————————————————————————————————

function CompletionCard({
  loading,
  summary,
  kycRejectedHandledByBanner,
  emailUnverified,
  email,
  onResendEmail,
  resendingEmail,
}: {
  loading: boolean;
  summary: ReturnType<typeof getCompletionSummary>;
  kycRejectedHandledByBanner?: boolean;
  emailUnverified?: boolean;
  email?: string | null;
  onResendEmail?: () => void;
  resendingEmail?: boolean;
  /** Free interview credits granted on first KYC approval (from public pricing). */
  verifyBonus?: number;
}) {
  const t = useTranslations("dashboard");
  // Default collapsed on mobile so the dashboard top stays compact and the
  // "Cần xử lý" list lands above the fold. User can tap header to expand.
  // Desktop (md+) ignores `open` and always shows the list.
  const [open, setOpen] = React.useState(false);

  if (loading) return null;

  const renderable = summary.tasks.filter((task) => {
    if (task.key === "kyc" && kycRejectedHandledByBanner) {
      return summary.kycRejected ? false : true;
    }
    return true;
  });

  const total = summary.total + (emailUnverified ? 1 : 0);
  const done = summary.done;
  if (total === 0) return null;
  if (done === total && !emailUnverified) return null;

  return (
    <Card
      tone="paper"
      padding="none"
      bordered
      className="overflow-hidden shadow-none"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center justify-between gap-3 border-b border-smoke bg-mist/40 px-5 py-4 text-left transition hover:bg-mist/60 md:cursor-default md:hover:bg-mist/40"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink text-paper">
            <Sparkles size={16} />
          </span>
          <div className="min-w-0">
            <p className="text-title-md font-bold text-ink">
              {t("completionTitle")}
            </p>
            <p className="text-xs text-ash">
              {t("completionSubtitle", { done, total })}
            </p>
          </div>
        </div>
        <div className="flex w-full items-center gap-2 md:w-auto">
          <ProgressRing done={done} total={total} />
          <ChevronDown
            size={18}
            className={
              "shrink-0 text-ash transition-transform md:hidden " +
              (open ? "rotate-180" : "")
            }
          />
        </div>
      </button>

      <ul
        className={
          "divide-y divide-smoke " + (open ? "block" : "hidden md:block")
        }
      >
        {renderable.map((task) => (
          <CompletionRow key={task.key} task={task} />
        ))}
        {emailUnverified ? (
          <EmailVerifyRow
            email={email ?? ""}
            onResend={onResendEmail ?? (() => {})}
            resending={!!resendingEmail}
          />
        ) : null}
      </ul>
    </Card>
  );
}

function ProgressRing({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="flex flex-1 items-center gap-2 md:flex-none">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-smoke md:w-32 md:flex-none">
        <div
          className="h-full bg-lime transition-[width]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums text-xs font-semibold text-ink">
        {pct}%
      </span>
    </div>
  );
}

function CompletionRow({ task }: { task: CompletionTask }) {
  const t = useTranslations("dashboard");
  const labelKey =
    task.key === "kyc"
      ? task.pending
        ? "completionTaskKycPending"
        : "completionTaskKyc"
      : "completionTaskTaxCode";
  const Icon = task.key === "kyc" ? ShieldCheck : FileBadge;

  const status = task.done ? "done" : task.pending ? "pending" : "todo";

  const inner = (
    <>
      <span
        className={
          "grid h-8 w-8 shrink-0 place-items-center rounded-full " +
          (status === "done"
            ? "bg-lime text-ink"
            : status === "pending"
              ? "bg-sun-light text-sun-dark"
              : "bg-mist text-ash")
        }
      >
        {status === "done" ? (
          <CheckCircle2 size={16} />
        ) : status === "pending" ? (
          <CircleDashed size={16} />
        ) : (
          <Icon size={16} />
        )}
      </span>
      <span className="flex-1 text-sm text-ink">{t(labelKey)}</span>
      {!task.done ? (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-ink">
          {t("completionCta")}
          <ChevronRight size={14} />
        </span>
      ) : null}
    </>
  );

  return (
    <li>
      {task.done ? (
        <div className="flex items-center gap-3 px-5 py-3">{inner}</div>
      ) : (
        <Link
          to={task.href}
          className="flex items-center gap-3 px-5 py-3 transition hover:bg-mist/60"
        >
          {inner}
        </Link>
      )}
    </li>
  );
}

function EmailVerifyRow({
  email,
  onResend,
  resending,
}: {
  email: string;
  onResend: () => void;
  resending: boolean;
}) {
  const t = useTranslations("auth");
  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-mist text-ash">
        <Mail size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink">{t("verifyEmailTask")}</p>
        {email ? <p className="truncate text-xs text-ash">{email}</p> : null}
      </div>
      <button
        type="button"
        onClick={onResend}
        disabled={resending}
        className="inline-flex items-center gap-0.5 rounded-full border border-smoke px-3 py-1.5 text-xs font-semibold text-ink transition hover:bg-mist disabled:opacity-60"
      >
        {resending ? t("verifyEmailResending") : t("verifyEmailResend")}
      </button>
    </li>
  );
}

// — Needs-action feed (mobile) ——————————————————————————————————————————

const FEED_LIMIT = 5;

// The post-completion hero on mobile: a short list of applicants still waiting
// on the employer, so an otherwise-quiet dashboard leads with a clear next move
// instead of four compact stat cards over empty space.
function NeedsActionFeed({
  items,
  totalCount,
  loading,
}: {
  items: PendingApplicant[];
  totalCount: number;
  loading: boolean;
}) {
  const t = useTranslations("dashboard");
  if (loading) return null;

  const shown = items.slice(0, FEED_LIMIT);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between px-1">
        <h2 className="text-title-md font-bold text-ink">{t("todoTitle")}</h2>
        {totalCount > 0 ? (
          <Link
            to="/applicants"
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-ink/70 transition hover:text-ink"
          >
            {t("needsActionViewAll", { count: totalCount })}
            <ChevronRight size={14} />
          </Link>
        ) : null}
      </div>

      {shown.length > 0 ? (
        <ul className="overflow-hidden rounded-2xl border-[1.5px] border-smoke bg-paper">
          {shown.map((a) => (
            <li
              key={a.applicationId}
              className="border-b border-smoke last:border-b-0"
            >
              <Link
                to="/applicants"
                className="flex items-center gap-3 px-4 py-3 transition hover:bg-mist/50"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-lime-light text-lime-dark">
                  <UserRound size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">
                    {a.workerMaskedName}
                  </p>
                  <p className="truncate text-xs text-ash">
                    {a.postingTitle} · {formatRelative(a.appliedAt)}
                  </p>
                </div>
                <ChevronRight size={18} className="shrink-0 text-ash" />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <NeedsActionEmpty />
      )}
    </div>
  );
}

function NeedsActionEmpty() {
  const t = useTranslations("dashboard");
  return (
    <div className="flex flex-col items-center rounded-2xl border-[1.5px] border-smoke bg-paper px-6 py-8 text-center">
      <span className="grid h-12 w-12 place-items-center rounded-full bg-lime text-ink">
        <CheckCircle2 size={22} />
      </span>
      <p className="mt-3 text-sm font-bold text-ink">
        {t("needsActionEmptyTitle")}
      </p>
      <p className="mt-1 max-w-xs text-xs text-ash">
        {t("needsActionEmptyBody")}
      </p>
      <Link
        to="/postings"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-smoke px-4 py-2 text-xs font-bold text-ink transition hover:bg-mist"
      >
        {t("needsActionEmptyCta")}
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

// — Onboarding empty-state ——————————————————————————————————————————————

function OnboardingPanel() {
  const t = useTranslations("dashboard");
  const steps = [
    {
      icon: <ClipboardList size={16} />,
      title: t("onboardingStep1"),
      body: t("onboardingStep1Body"),
    },
    {
      icon: <ListChecks size={16} />,
      title: t("onboardingStep2"),
      body: t("onboardingStep2Body"),
    },
    {
      icon: <TicketCheck size={16} />,
      title: t("onboardingStep3"),
      body: t("onboardingStep3Body"),
    },
  ];
  return (
    <div className="px-5 py-6">
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-title-md font-bold text-ink">
          {t("onboardingTitle")}
        </p>
        <p className="mt-1 text-xs text-ash">{t("onboardingSubtitle")}</p>
      </div>
      <ol className="mt-5 grid gap-3 sm:grid-cols-3">
        {steps.map((s, i) => (
          <li
            key={i}
            className="rounded-2xl border-[1.5px] border-smoke bg-mist/30 p-4"
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-ink text-paper text-xs font-semibold">
                {i + 1}
              </span>
              <span className="text-ash">{s.icon}</span>
            </div>
            <p className="text-sm font-semibold text-ink">{s.title}</p>
            <p className="mt-1 text-xs text-ash">{s.body}</p>
          </li>
        ))}
      </ol>
      <div className="mt-5 text-center">
        <Link
          to="/postings/new"
          className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-ink/90"
        >
          <Plus size={16} />
          {t("ctaCreatePosting")}
        </Link>
      </div>
    </div>
  );
}
